<script setup lang="ts">
import { reset } from '@formkit/core'
import { modelMapper, type ModelData } from '~/models'
import type { NuxtError } from 'nuxt/app'
import type { Post } from '~/models/post'
import type { User } from '~/models/user'

useTitle('support.title', { t: true })

const { t } = useI18n()
const apiClient = useAPIClient()
const userState = useUserState()
const flash = useFlash()

interface State {
  user?: User|null
  faqPosts?: Post[]
  selectedPostId?: number
  category?: string
}

const state: State = reactive({})
const { errors, setFieldErrors } = useFieldErrors()

// Fetch posts
const { data, error } = await useAsyncData('faqPosts', () => {
  return apiClient.fetch('posts?category=faqs')
})
if (error.value) throw error.value

if (data.value) {
  state.faqPosts = (data.value as ModelData[]).map(d => modelMapper.Post(d))
}

// Fetch user if authenticated
if (userState.value.tokenPair) {
  const { user } = await apiClient.user()
  state.user = user.value
}

const ticketKinds = [
  { value: 'sales', label: t('support.categories.sales') },
  { value: 'billing', label: t('support.categories.billing') },
  { value: 'technical', label: t('support.categories.technical') },
  { value: 'feature_request', label: t('support.categories.feature_request')},
  { value: 'other', label: t('support.categories.other') },
]

const appVersions = [
  { value: '3.1', label: 'PingZapper 3.1' },
  { value: '3.0', label: 'PingZapper 3.0' },
  { value: '2', label: `PingZapper 2 ${t('download.legacy')}`}
]

const messagePlaceholder = computed(() => {
  if (state.category === 'technical')
    return t('support.technical_placeholder')
  return ''
})

interface TicketFields {
  email: string
  kind: string
  message: string
  appVersion?: string
  orderId?: string
  attachment?: { file: File, name: string }[]
}

function togglePost(post: Post) {
  if (isPostSelected(post)) {
    state.selectedPostId = undefined
  } else {
    state.selectedPostId = post.id
  }
}

function isPostSelected(post: Post) {
  return state.selectedPostId === post.id
}

async function submitTicket(fields: TicketFields) {
  const body = new FormData()
  body.append('email', fields.email)
  body.append('kind', fields.kind)
  body.append('message', fields.message)

  if (fields.kind === 'billing' && fields.orderId)
    body.append('orderId', fields.orderId)

  if (fields.kind === 'technical') {
    if (fields.appVersion) body.append('appVersion', fields.appVersion)
    if (fields.attachment?.length) {
      body.append('attachment', fields.attachment[0].file)
    }
  }

  try {
    await apiClient.fetch('tickets', { method: 'POST', body })
    flash.set(
      t('support.ticket_submitted'), { kind: 'success', immediate: true }
    )
    reset('ticket-form')
  } catch (e: unknown) {
    const err = e as NuxtError
    if (err.statusCode === 413) {
      flash.set(
        t('errors.status_413'), { kind: 'error', immediate: true }
      )
      return
    }

    if (!setFieldErrors('users', err)) {
      return showError(err)
    }
  }
}
</script>

<template>
  <div class="support">
    <div class="faq page-section">
      <PageHeader :heading="t('support.title')" />

      <h2>{{ $t('support.faqs') }}</h2>

      <div v-if="state.faqPosts?.length" class="faqs">
        <div
          v-for="post of state.faqPosts"
          class="post"
        >
          <header @click="togglePost(post)">
            <h3>{{ post.title }}</h3>

            <Icon v-if="isPostSelected(post)" name="collapse-arrow" />
            <Icon v-else name="expand-arrow" />
          </header>
          <div v-if="isPostSelected(post)" class="content">
            <marked-view :content="post.content" />
          </div>
        </div>
      </div>
    </div>

    <div class="ticket page-section">
      <div class="left">
        <h2>{{ $t('support.contact_us') }}</h2>

        <div class="sign-in">
          <template v-if="!state.user">
            <p>
              {{ $t('support.have_an_account') }}
              <br>
              {{ $t('support.support_signin') }}
            </p>
            <NuxtLinkLocale href="/sign_in?redirect=/support" class="button">
              {{ $t('users.sign_in') }}
            </NuxtLinkLocale>
          </template>
          <template v-else>
            {{ $t('support.personalized_support', { username: state.user.username }) }}
          </template>
        </div>
      </div>
      <div class="ticket-form right">
        <FlashMessage />

        <FormKit
          type="form"
          id="ticket-form"
          ref="ticketForm"
          :submit-attrs="{ inputClass: 'button' }"
          @submit="submitTicket"
          class="formkit-form"
        >
          <FormKit
            name="email"
            type="text"
            :label="$t('support.email')"
            validation="required|email"
            :value="state.user?.email"
            :disabled="state.user != null"
          />

          <FormKit
            name="kind"
            type="select"
            v-model="state.category"
            :label="$t('support.category')"
            :placeholder="$t('support.select_category')"
            :options="ticketKinds"
            validation="required"
          />
          <FormKit
            v-show="state.category === 'billing'"
            name="orderId"
            type="text"
            :label="$t('support.order_id')"
          />
          <FormKit
            v-show="state.category === 'technical'"
            name="appVersion"
            type="select"
            :placeholder="$t('support.select_app_version')"
            :label="$t('support.app_version')"
            :options="appVersions"
            :validation="state.category == 'technical' ? 'required' : ''"
          />
          <FormKit
            name="message"
            type="textarea"
            :label="$t('support.message')"
            :placeholder="messagePlaceholder"
            validation="required"
          />
          <FormKit
            v-show="state.category === 'technical'"
            name="attachment"
            type="file"
            :label="`${$t('support.attachment')} ${$t('support.attachment_max_size')}`"
            multiple="false"
            accept=".bmp,.jpg,.png,.heic,.pdf,.txt,.mp4,.mov,.heif"
          />

          <Captcha
            :show="apiClient.showCaptcha.value"
            @verified="(p: string) => apiClient.captchaPayload.value = p"
          />
        </FormKit>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.faqs {
  .post {
    header {
      display: flex;
      justify-content: space-between;
      padding-top: 1rem;
      border-top: 1px solid $color-medium-gray;
      cursor: pointer;
    }

    .content {
      margin: 0 1rem;
    }
  }
}

.ticket.page-section {
  @media (min-width: $breakpoint-md) {
    display: flex;
    justify-content: space-between;

    .left {
      padding: 1rem 2rem 1rem 1rem;
    }

    .right {
      padding: 1rem;
    }

    .left { flex: 1 1 40%; }
    .right { flex: 1 1 60%; }
  }
}

.ticket-form {
  :deep(textarea[name="message"]) {
    width: 100%;
    height: 15rem;
  }

  // .actions {
  //   display: flex;
  //   justify-content: space-between;
  //   align-items: flex-end;
  //   margin-top: 1rem;

  //   .altcha-widget { flex: 1 1 auto; }
  // }
}
</style>
